import Guid from '../utils/Guid'
import { inject } from 'vue'
import { Author, ExtractedAuthor, ExtractedAffiliation, Country, Institute, LocalStorage, Reviewer, ReviewerType, State, Submission, SubmissionFileType, ShowToastType } from '@/types'
import submissionApi from '../api/submission-api'
import { Commit } from 'vuex/types/index.js'
import countriesList from '../data/countries.json'
import { sanitiseAuthorsEmails } from '@/utils/sanitiseEmail'

const supportingFileTypes = [
  SubmissionFileType.CrystalData,
  SubmissionFileType.Esi,
  SubmissionFileType.Figure,
  SubmissionFileType.CoverLetter,
]

export function ensureAuthorsSanitised(submission: Submission) {
  if (!submission?.authors?.length)
    return
  ensureNotDuplicatedById(submission)
  ensureAffiliationNotNull(submission)
  sanitiseAuthorsEmails(submission)
}

function ensureNotDuplicatedById(submission: Submission) {
  submission.authors = distinct(submission.authors, author => author.id)
}

function ensureAffiliationNotNull(submission: Submission) {
  submission.authors.forEach((author) => {
    author.affiliation ??= defaultAffiliation()
    if (author.affiliation.country && !Object.keys(author.affiliation.country))
      author.affiliation.country = undefined// For later filtering on EditAuthor component
  })
}

function handleAuthorCorresponding(authors: ExtractedAuthor[]) {
  if (!authors || authors.length === 0) return

  const correspondingAuthors = authors.filter(author => author.isCorresponding)

  if (correspondingAuthors.length === 1) {
    return
  }
  else if (correspondingAuthors.length) {
    correspondingAuthors.forEach(correspondingAuthor => correspondingAuthor.isCorresponding = false)
    correspondingAuthors[correspondingAuthors.length - 1].isCorresponding = true
  }
  else {
    authors[authors.length - 1].isCorresponding = true
  }
}

function convertExtractedAffiliationToInstitute(extractedAffiliation: ExtractedAffiliation): Institute {
  const countries = countriesList as Country[]
  const country = countries.find(c => c.id === extractedAffiliation.countryId)

  return {
    name: extractedAffiliation.institution || extractedAffiliation.institution || '',
    city: extractedAffiliation.city || '',
    country: country,
  }
}

export function defaultAffiliation(): Institute {
  return {
    name: '',
    city: '',
    country: undefined,
  }
}

export function defaultAuthor(): Author {
  return {
    id: Guid.generate(),
    firstName: '',
    initials: '',
    lastName: '',
    isCorresponding: false,
    email: '',
    orcid: '',
    affiliation: defaultAffiliation(),
  }
};

export function defaultReviewer(reviewerType: ReviewerType = ReviewerType.Recommend): Reviewer {
  return {
    id: Guid.generate(),
    firstName: '',
    lastName: '',
    email: '',
    institution: '',
    type: reviewerType,
    reason: '',
  }
};

export async function uploadFileToSubmission(commit: Commit, state: State, file: File, fileType: SubmissionFileType, tempId: Guid) {
  if (fileType === SubmissionFileType.MainArticle) {
    const previousTitle = state.activeSubmission.title || ''
    const previousAbstract = state.activeSubmission.abstract || ''
    const previousAuthors = state.activeSubmission.authors || []

    const hasPreviousData
      = previousTitle.trim().length > 0
        || previousAbstract.trim().length > 0
        || previousAuthors.length > 0

    if (hasPreviousData) {
      commit('_setDataToCompare', {
        title: previousTitle,
        abstract: previousAbstract,
        authors: JSON.parse(JSON.stringify(previousAuthors)),
        ingestedAt: new Date().toISOString(),
      })
    }
  }
  return submissionApi.addFile(state.activeSubmission.submissionId, file, fileType).then(async (response) => {
    commit('_fileUploadSuccess', { oldId: tempId, newId: response.data.fileId })
    const supportingFile = supportingFileTypes.find(type => type === fileType)
    if (supportingFile) {
      return
    }

    if (fileType !== SubmissionFileType.DAS) {
      commit('setFinalReviewVisited', false)
    }

    if (fileType === SubmissionFileType.MainArticle) {
      commit('_setTitle', response.data.title ?? '')
      commit('_setAbstract', response.data.abstract ?? '')
      commit('_deleteAuthors')
      handleAuthorCorresponding(response.data.authors ?? []);

      (response.data.authors ?? []).forEach((a) => {
        // Use the first affiliation if available, otherwise create empty affiliation
        const extractedAffiliation = a.affiliations && a.affiliations.length > 0 ? a.affiliations[0] : null
        const affiliation: Institute = extractedAffiliation
          ? convertExtractedAffiliationToInstitute(extractedAffiliation)
          : defaultAffiliation()

        const author: Author = {
          id: Guid.generate(),
          firstName: a.firstName || '',
          initials: '',
          lastName: a.lastName || '',
          isCorresponding: !!a.isCorresponding,
          email: a.email || '',
          orcid: '',
          affiliation,
        }
        commit('_addAuthor', author)
      })
      const extractedTitle = response.data.title ?? ''
      const extractedAbstract = response.data.abstract ?? ''
      const extractedAuthors = response.data.authors ?? []
      const hasTitle = extractedTitle && extractedTitle.trim().length > 0
      const hasAbstract = extractedAbstract && extractedAbstract.trim().length > 0
      const hasAuthors = extractedAuthors && extractedAuthors.length > 0
      const snapshot: { title?: boolean, abstract?: boolean, authors?: boolean } = {}
      if (hasTitle) snapshot.title = true
      if (hasAbstract) snapshot.abstract = true
      if (hasAuthors) snapshot.authors = true

      if (hasTitle || hasAbstract || hasAuthors) {
        commit('_setExtractedSnapshot', snapshot)
      }
      else {
        commit('_setExtractedSnapshot', {})
      }
      const hasExtracted = !!(
        response.data.title?.trim()
        || response.data.abstract?.trim()
        || (response.data.authors && response.data.authors.length > 0)
      )
      commit('_setHasExtractedDataFromFile', hasExtracted)
      if (hasExtracted) {
        commit('setModalShownSinceExtraction', true)
      }
    }
  })
    .catch(async (err) => {
      if (err.response.status === 409) {
        commit('_fileUploadDuplicate', tempId)
      }
      else {
        commit('_fileUploadFailure', tempId)
      }
      throw err
    })
}

export function distinct<T, U>(array: T[], itemConversionHandler: (item: T) => U): T[] {
  if (!array.length)
    return array

  const uniqueConvertedValues = array.map(itemConversionHandler).filter((value, index, self) => self.indexOf(value) === index)

  if (array.length === uniqueConvertedValues.length)
    return array

  const distinctArray = uniqueConvertedValues.map(convertedValue => array.filter(item => itemConversionHandler(item) === convertedValue)[0])

  return distinctArray
}

export function submissionFileTypeToNumber(submissionFileType: SubmissionFileType): number {
  switch (submissionFileType) {
    case SubmissionFileType.CoverLetter: {
      return 1
    }
    case SubmissionFileType.MainArticle: {
      return 2
    }
    case SubmissionFileType.DAS: {
      return 3
    }
    case SubmissionFileType.Figure: {
      return 4
    }
    case SubmissionFileType.Esi: {
      return 5
    }
    case SubmissionFileType.CrystalData: {
      return 6
    }
    default:
      throw new Error(`Unknown submission file type: ${submissionFileType}`)
  }
}

export function indexOfByPredicate<T>(array: T[], predicate: (item: T) => boolean): number | null {
  if (!array.length)
    return null

  const filteredArray = array.filter(predicate)

  if (!filteredArray.length)
    return null

  return array.indexOf(filteredArray[0])
}

export function identifyRouteFromStep(localStorageData: LocalStorage): string {
  const localDataSubmission: State = localStorageData.data
  switch (localDataSubmission.step) {
    case 1: {
      return 'upload-files'
    }
    case 2: {
      return 'article-info'
    }
    case 3: {
      return 'author-info'
    }
    case 4: {
      return 'review-editorial'
    }
    case 5: {
      return 'final-review'
    }
  }

  return '0'
}

export const usePreventNavigation = () => {
  const showToast = inject('showToast') as (options: ShowToastType) => void
  if (!showToast) {
    throw new Error('showToast not provided in app context')
  }

  const preventNavigation = (options: {
    headingText: string
    bodyContent: string
    location: string | SubmissionFileType
  }): void => {
    showToast({
      variant: 'warning',
      headingText: options.headingText,
      bodyContent: options.bodyContent,
      location: options.location,
      isManual: false,
      isUpload: true,
    })
  }

  return { preventNavigation }
}
